import { useCallback, useEffect, useRef, useState } from "react";

import { ServerMessageSchema } from "@frontierx/shared/schemas/ServerMessage";
import type { SummaryOutput } from "@frontierx/shared/schemas/Summary";
import {
  type TranscriptUpdate,
  TranscriptUpdateSchema,
} from "@frontierx/shared/schemas/TranscriptUpdate";
import type {
  BaseClientMessage,
  ClientMessage,
} from "@frontierx/shared/schemas/clientMessageTypes";
import {
  type AILatencyMetrics,
  type AIQuestionAnswer,
  AIQuestionAnswerSchema,
  type AIStreamingStatus,
  type CallQuestion,
  type MeetingParticipant,
  type PartialAIResponse,
  type PlaybookProgress,
  PlaybookProgressSchema,
  type PlaybookScriptItem,
  PlaybookSectionSchema,
  type RecallBot,
  RecallBotSchema,
  StatusChangeSchema,
} from "@frontierx/shared/types";
import { updateTranscriptArray } from "@frontierx/shared/utils/updateTranscript";

import type PartySocket from "partysocket";
import { useLocalStorage } from "usehooks-ts";
import { z } from "zod";
import { useAppPartySocket } from "~/app/hooks/useAppPartySocket";
import { useBotStatusPolling } from "~/app/hooks/useBotStatusPolling";
import { useAuth } from "../../Auth/UnifiedAuthProvider";

import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useLiveGuidance } from "../../../hooks/useLiveGuidance";
import { useAnalytics } from "../../Analytics/AnalyticsProvider";
import type {
  HandleGetHelpFunction,
  HandleGetHelpFunctionArgs,
} from "../../Help/AiHelpCard";

export type PartyServerConnectionStatus =
  | "connected"
  | "disconnected"
  | "reconnecting";

// Define a type for the botSocket
type BotSocketType = Partial<PartySocket> & {
  send: (message: string) => void;
  close: () => void;
  reconnect: () => void;
};

export type UseCallDisplayReturn = {
  botSocket: BotSocketType;
  transcript: TranscriptUpdate[];
  roomBot: RecallBot | null;
  aiHistory: AIQuestionAnswer[];
  error: string | null;
  clearError: () => void;
  streamingResponse: PartialAIResponse | null;
  isStreaming: boolean;
  streamingStatus: AIStreamingStatus | undefined;
  latencyMetrics: AILatencyMetrics | undefined;
  messageLatency: number;
  handleGetHelp: HandleGetHelpFunction;
  handleRequestBotInfo: () => void;
  handleCompleteScriptItem: (scriptItem?: PlaybookScriptItem) => void;
  handleSetCurrentScriptItem: (scriptItem?: PlaybookScriptItem) => void;
  handleToggleProcessScript: () => void;
  handleRequestSummary: () => void;
  participants: MeetingParticipant[];
  playbook: PlaybookProgress;
  processScript: boolean;
  currentScriptItem: PlaybookScriptItem | undefined;
  partyServerConnectionStatus: PartyServerConnectionStatus;
  isPolling: boolean;
  questions: CallQuestion[];
  reconnectAttempt: number;
  summary: SummaryOutput | null;
  currentAiHelpRequest: HandleGetHelpFunctionArgs | undefined;
  participantRoles: {
    assignments: Array<{
      recallParticipantId: string;
      role: "salesperson" | "client" | "observer";
      displayName: string;
      isCurrentUser?: boolean;
      assignedBy?: string;
      assignedAt?: string;
    }>;
    participants: Array<{
      id: number;
      name: string;
      role: "salesperson" | "client" | "observer";
      isHost: boolean;
      hasExplicitAssignment: boolean;
      assignedAt?: string;
    }>;
  } | null;
  scriptDetectionLogs: Array<{
    timestamp: number;
    scriptItemId: string;
    scriptItemText: string;
    triggeringText: string;
    checksPerformed: Array<{
      method: string;
      score: number;
      partialThreshold: number;
      fullThreshold: number;
    }>;
    decision: "completed" | "partial" | "no_match";
    reason: string;
    similarity?: number;
  }>;
};

export function useCallDisplay(roomId: string): UseCallDisplayReturn {
  const { organization } = useAuth();
  const orgId = organization?.id;
  const { trackEvent } = useAnalytics();

  // Add debug logging for the roomId
  console.log(
    "[CallDisplay] Initializing with roomId:",
    roomId,
    "orgId:",
    orgId,
  );

  const [transcript, setTranscript] = useState<TranscriptUpdate[]>([]);
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [processScript, setProcessScript] = useState<boolean>(false);
  const [roomBot, setRoomBot] = useState<RecallBot | null>(null);
  const [aiHistory, setAIHistory] = useState<AIQuestionAnswer[]>([]);
  const [questions, setQuestions] = useState<CallQuestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [streamingResponse, setStreamingResponse] =
    useState<PartialAIResponse | null>(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamingStatus, setStreamingStatus] = useState<
    AIStreamingStatus | undefined
  >(undefined);
  const [currentAiHelpRequest, setCurrentAiHelpRequest] = useState<
    HandleGetHelpFunctionArgs | undefined
  >(undefined);
  const streamingTimeoutRef = useRef<number | null>(null);
  const [messageLatency, setMessageLatency] = useState(0);
  const resetStreamingTimeout = useCallback(() => {
    if (isStreaming) {
      if (streamingTimeoutRef.current !== null) {
        window.clearTimeout(streamingTimeoutRef.current);
      }
      streamingTimeoutRef.current = window.setTimeout(() => {
        if (isStreaming) {
          setIsStreaming(false);
          setCurrentAiHelpRequest(undefined);
          setStreamingResponse(null);
        }
      }, 20000);
    } // 20 seconds timeout
  }, [isStreaming]);

  const [latencyMetrics, setLatencyMetrics] = useState<
    AILatencyMetrics | undefined
  >(undefined);
  const [playbook, setPlaybook] = useState<PlaybookProgress>({
    sections: [],
    currentSectionId: null,
    status: "in_progress",
  });

  const clearError = useCallback(() => setError(null), []);

  const [currentScriptItem, setCurrentScriptItem] =
    useState<PlaybookScriptItem | null>(null);

  const [partyServerConnectionStatus, setPartyServerConnectionStatus] =
    useState<PartyServerConnectionStatus>("disconnected");
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const [summary, setSummary] = useState<SummaryOutput | null>(null);
  const [participantRoles, setParticipantRoles] = useState<{
    assignments: Array<{
      recallParticipantId: string;
      role: "salesperson" | "client" | "observer";
      displayName: string;
      isCurrentUser?: boolean;
      assignedBy?: string;
      assignedAt?: string;
    }>;
    participants: Array<{
      id: number;
      name: string;
      role: "salesperson" | "client" | "observer";
      isHost: boolean;
      hasExplicitAssignment: boolean;
      assignedAt?: string;
    }>;
  } | null>(null);

  // Add state for script detection debugging logs
  const [scriptDetectionLogs, setScriptDetectionLogs] = useState<
    Array<{
      timestamp: number;
      scriptItemId: string;
      scriptItemText: string;
      triggeringText: string;
      checksPerformed: Array<{
        method: string;
        score: number;
        partialThreshold: number;
        fullThreshold: number;
      }>;
      decision: "completed" | "partial" | "no_match";
      reason: string;
      similarity?: number;
    }>
  >([]);

  useEffect(() => {
    const handleOnline = () => {
      console.log("Network is online. Attempting to reconnect...");
      // Attempt to reconnect?
    };

    const handleOffline = () => {
      console.log("Network is offline. WebSocket may disconnect.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const [useRealtimeAPIRaw] = useLocalStorage("use-realtime-api", false);
  const useRealtimeAPI: boolean = useRealtimeAPIRaw === true;

  const hasRealtimeFeatureFlag = useFeatureFlag("realtime-api");

  // Log the parameters we're going to use
  console.log("[CallDisplay] Attempting to create socket with:", {
    roomId,
    party: "call",
    // The expected URL format in PartyServer is: /parties/[party]/[room]
    expectedPath: `/parties/call/${roomId}`,
  });

  // Always call hooks at the top level, not in conditionals
  const botSocket: BotSocketType = useAppPartySocket({
    room: roomId,
    party: "call",
    onOpen() {
      console.log(
        "[CallDisplay] WebSocket connection opened for room:",
        roomId,
      );
      setPartyServerConnectionStatus("connected");
      setReconnectAttempt(0);
    },
    onClose() {
      console.warn(
        "[CallDisplay] WebSocket connection closed for room:",
        roomId,
      );
      setPartyServerConnectionStatus("disconnected");
      handleReconnect();
    },
    onError(error) {
      console.error("[CallDisplay] WebSocket error for room:", roomId, error);
      setError("WebSocket connection error. Some features may be unavailable.");
    },
    onMessage(event) {
      try {
        const data = JSON.parse(event.data);
        const message = ServerMessageSchema.parse(data);
        const messageCopy = JSON.parse(JSON.stringify(message));
        console.log(message.type, messageCopy);

        switch (message.type) {
          case "CALL_SUMMARY":
            setSummary(message.payload);
            break;
          case "PROCESS_SCRIPT":
            setProcessScript(message.payload);
            break;
          case "FULL_TRANSCRIPT":
            setTranscript(
              z.array(TranscriptUpdateSchema).parse(message.payload),
            );
            break;
          case "TRANSCRIPT_UPDATE":
            setTranscript((prev) => {
              console.log(
                "Setting transcript",
                prev.map((item) => `${item.id}:${item.is_final}`),
              );
              const updatedItem = TranscriptUpdateSchema.parse(message.payload);
              // Only update final items
              // if (!updatedItem.is_final) {
              //   return prev;
              // }

              // Transcript updates are now sent directly from the call server to the feedback server
              // This eliminates the extra network hop and reduces latency
              if (!(hasRealtimeFeatureFlag && useRealtimeAPI)) {
                console.log("Realtime API disabled");
              }
              return updateTranscriptArray(prev, updatedItem);
            });
            break;
          case "BOT_INFO":
            setRoomBot(RecallBotSchema.parse(message.payload));
            break;
          case "STATUS_UPDATE": {
            const newStatus = StatusChangeSchema.parse(message.payload.status);
            setRoomBot((prevBot) =>
              prevBot
                ? {
                    ...prevBot,
                    status_changes: [
                      ...(prevBot.status_changes ?? []),
                      newStatus,
                    ],
                  }
                : null,
            );
            break;
          }
          case "AI_HISTORY":
            setAIHistory(message.payload);

            break;
          case "AI_HELP_STREAM_INFO":
            setStreamingStatus(message.payload);

            break;
          case "AI_HELP_STREAM":
            setIsStreaming(true);
            setStreamingResponse(message.payload);
            resetStreamingTimeout();
            break;
          case "AI_ERROR": {
            setIsStreaming(false);
            setStreamingStatus(undefined);
            setCurrentAiHelpRequest(undefined);
            setStreamingResponse(null);
            if (streamingTimeoutRef.current !== null) {
              window.clearTimeout(streamingTimeoutRef.current);
            }
            break;
          }
          case "AI_HELP_STREAM_END": {
            setIsStreaming(false);
            setStreamingResponse(message.payload);
            setStreamingStatus(undefined);
            setCurrentAiHelpRequest(undefined);
            if (streamingTimeoutRef.current !== null) {
              window.clearTimeout(streamingTimeoutRef.current);
            }
            const parsed = AIQuestionAnswerSchema.safeParse(message.payload);
            if (parsed.success) {
              setAIHistory((prevHistory) => [...prevHistory, parsed.data]);
            }
            setStreamingResponse(null);

            break;
          }
          case "MESSAGE_LATENCY":
            console.log("Message Latency", message.payload);
            setMessageLatency(message.payload);
            break;
          case "AI_LATENCY_METRICS":
            setLatencyMetrics(message.payload);
            trackEvent("room_ai_latency_metrics", message.payload);
            break;
          case "ERROR":
            setError(message.payload.message);
            setIsStreaming(false);
            if (streamingTimeoutRef.current !== null) {
              window.clearTimeout(streamingTimeoutRef.current);
            }
            console.error(
              "Received error from server:",
              message.payload.message,
            );
            trackEvent("room_error", { message: message.payload.message });
            break;
          case "PLAYBOOK_PROGRESS":
            setPlaybook(PlaybookProgressSchema.parse(message.payload));
            break;
          case "CURRENT_PLAYBOOK_SECTION":
            setPlaybook((prev) => {
              return {
                ...prev,
                currentSectionId: message.payload,
              };
            });
            break;
          case "PLAYBOOK_SECTION_UPDATE":
            setPlaybook((prev) => {
              const updatedSection = PlaybookSectionSchema.parse(
                message.payload,
              );
              return {
                ...prev,
                sections: prev.sections.map((section) =>
                  section.id === updatedSection.id ? updatedSection : section,
                ),
              };
            });
            break;
          case "CURRENT_SCRIPT_ITEM":
            console.log("Setting current script item:", message.payload);
            setCurrentScriptItem(message.payload);
            break;
          case "CALL_QUESTIONS_UPDATE":
            setQuestions(data.payload);
            break;
          case "PARTICIPANTS":
            setParticipants(data.payload);
            break;
          case "PLAYBOOK_COMPLETE":
            // Handle playbook completion (e.g., show a completion message)
            break;
          // Ignore messages that are specifically for the FeedbackServer and not intended for client handling
          case "INITIALIZE_FEEDBACK":
          case "REJOIN_CALL":
            // These messages are meant for the server, not for client-side handling
            console.log("Ignoring server-bound message:", message.type);
            break;
          case "PARTICIPANT_ROLES_RESPONSE":
            console.log(
              "📨 Received PARTICIPANT_ROLES_RESPONSE:",
              message.payload,
            );
            setParticipantRoles(message.payload);
            break;
          case "PARTICIPANT_ROLES_UPDATED":
            console.log(
              "📨 Received PARTICIPANT_ROLES_UPDATED:",
              message.payload,
            );
            // PARTICIPANT_ROLES_UPDATED only sends assignments, so request full data
            if (botSocket?.send) {
              console.log("📤 Requesting GET_PARTICIPANT_ROLES after update");
              botSocket.send(
                JSON.stringify({
                  type: "GET_PARTICIPANT_ROLES",
                  payload: {},
                  timestamp: new Date().toISOString(),
                }),
              );
            }
            break;
          case "SCRIPT_DETECTION_LOG":
            console.log("📨 Received SCRIPT_DETECTION_LOG:", message.payload);
            setScriptDetectionLogs((prev) =>
              [...prev, message.payload].slice(-100),
            ); // Keep last 100 logs
            break;
          default:
            console.warn(
              "Unhandled message type:",
              (message as { type: string }).type,
            );
        }
      } catch (error) {
        console.error("Error processing message:", error);
      }
    },
  });

  // Error handling via useEffect
  useEffect(() => {
    // If the socket connection fails, it will already be handled by onError
    // This just provides additional logging
    console.log("[CallDisplay] Socket initialized for room:", roomId);

    // Check if socket has expected properties
    if (!botSocket || typeof botSocket.send !== "function") {
      console.error("[CallDisplay] Socket failed to initialize properly");
      setError(
        "WebSocket connection failed. Real-time features may be unavailable.",
      );
      setPartyServerConnectionStatus("disconnected");
    }

    return () => {
      console.log("[CallDisplay] Cleaning up socket for room:", roomId);
    };
  }, [roomId, botSocket]);

  const _guidanceSocket = useLiveGuidance({
    roomId,
    isEnabled: !!(hasRealtimeFeatureFlag && useRealtimeAPI),
    hostName: "SDR",
    companyName: "Frontier AI",
    debug: true,
  });

  const handleReconnect = useCallback(() => {
    setPartyServerConnectionStatus("reconnecting");
    const backoffTime = Math.min(1000 * 2 ** reconnectAttempt, 30000); // Max 30 seconds
    setTimeout(() => {
      setReconnectAttempt((prev) => prev + 1);
      botSocket.reconnect();
    }, backoffTime);
  }, [botSocket, reconnectAttempt]);

  useEffect(() => {
    console.log("Current script item:", currentScriptItem);
  }, [currentScriptItem]);

  const sendClientEvent = useCallback(
    (message: BaseClientMessage) => {
      const messageWithTimestamp: ClientMessage = {
        ...message,
        timestamp: new Date().toISOString(),
      };
      console.log("Sending client event:", messageWithTimestamp);
      try {
        botSocket.send(JSON.stringify(messageWithTimestamp));
      } catch (error) {
        console.error("Failed to send client event:", error);
      }
    },
    [botSocket],
  );
  const handleToggleProcessScript = useCallback(() => {
    setProcessScript((prevProcessScript) => {
      const newProcessScript = !prevProcessScript;
      sendClientEvent({ type: "PROCESS_SCRIPT", payload: newProcessScript });
      return newProcessScript;
    });
  }, [sendClientEvent]);

  const handleGetHelp = useCallback(
    (args: HandleGetHelpFunctionArgs) => {
      const { question, transcriptItemIds, detectedQuestion } = args;

      if (!orgId) {
        console.error("No orgId found");
        return;
      }
      const message: ClientMessage = {
        type: "GET_HELP",
        timestamp: new Date().toISOString(),
        payload: {
          orgId,
          question,
          transcriptItemIds,
          detectedQuestion,
        },
      };

      setCurrentAiHelpRequest(args);
      sendClientEvent(message);
      setIsStreaming(true);
      setStreamingResponse(null);
      setError(null);
      resetStreamingTimeout();
    },
    [resetStreamingTimeout, sendClientEvent, orgId],
  );

  useEffect(() => {
    return () => {
      if (streamingTimeoutRef.current !== null) {
        window.clearTimeout(streamingTimeoutRef.current);
      }
    };
  }, []);

  const handleRequestBotInfo = useCallback(() => {
    sendClientEvent({ type: "REQUEST_BOT_INFO" });
  }, [sendClientEvent]);

  const handleRequestSummary = useCallback(() => {
    sendClientEvent({ type: "REQUEST_SUMMARY" });
  }, [sendClientEvent]);

  const handleCompleteScriptItem = useCallback(
    (scriptItem?: PlaybookScriptItem) => {
      if (!scriptItem) {
        return;
      }
      if (scriptItem.completed) {
        sendClientEvent({
          type: "UNCOMPLETE_SCRIPT_ITEM",
          payload: scriptItem,
        });
      } else {
        sendClientEvent({
          type: "COMPLETE_SCRIPT_ITEM",
          payload: scriptItem,
        });
      }
    },
    [sendClientEvent],
  );

  const handleSetCurrentScriptItem = useCallback(
    (scriptItem?: PlaybookScriptItem) => {
      if (!scriptItem) {
        return;
      }
      sendClientEvent({
        type: "SET_CURRENT_SCRIPT_ITEM",
        payload: scriptItem,
      });
    },
    [sendClientEvent],
  );

  const { isPolling } = useBotStatusPolling({
    roomBot,
    onPoll: handleRequestBotInfo,
  });

  return {
    botSocket,
    transcript,
    roomBot,
    aiHistory,
    error,
    clearError,
    streamingResponse,
    isStreaming,
    streamingStatus,
    latencyMetrics,
    messageLatency,
    handleGetHelp,
    handleRequestBotInfo,
    handleCompleteScriptItem,
    handleSetCurrentScriptItem,
    handleToggleProcessScript,
    handleRequestSummary,
    playbook,
    participants,
    processScript,
    currentScriptItem: currentScriptItem ?? undefined,
    partyServerConnectionStatus,
    isPolling,
    questions,
    reconnectAttempt,
    summary,
    currentAiHelpRequest,
    participantRoles,
    scriptDetectionLogs,
  };
}
